import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { action, extendObservable, observable } from 'mobx'
import Tooltip from '@components/Tooltip'
import Button from '@components/Button'
import RecordingsProcessing from '@components/RecordingsProcessing'
import PropTypes from 'prop-types'
import { eplApi } from '@atheneum/api'

import debounce from 'lodash/debounce'
import ToggleVisibility from './ToggleVisibility'
import moment from 'moment'

const SYNC_RECORDINGS_DEBOUNCE_DELAY_IN_MS = 1000

class RecordingsSection extends Component {
  static propTypes = {
    form: PropTypes.object,
    growlStore: PropTypes.object,
    fieldHiddenToExpert: PropTypes.bool,
    toggleFieldsHiddenToExpert: PropTypes.func,
    recordingEnd: PropTypes.string
  }

  constructor (props) {
    super(props)

    const eplId = this.props.form.values.id

    extendObservable(this, {
      loading: false,
      error: null,
      count: observable.box(0),
      recordingClickCounts: {
        video: 0,
        audio: 0,
        transcript: 0
      },
      recordingCounts: {
        total: 0,
        audio: 0,
        video: 0,
        transcript: 0
      }
    })

    this.getClickCountsForEpl(eplId)

    this.loadRecordings(eplId)
  }

  getClickCountsForEpl (eplId) {
    return Promise.all([
      eplApi.getLoggedEvents('epl_recording_mp4_click', eplId),
      eplApi.getLoggedEvents('epl_recording_m4a_click', eplId),
      eplApi.getLoggedEvents('epl_recording_transcript_click', eplId)
    ]).then(([[videoClicks], [audioClicks], [transcriptClicks]]) => {
      this.recordingClickCounts = {
        video: (videoClicks && videoClicks.count) || 0,
        audio: (audioClicks && audioClicks.count) || 0,
        transcript: (transcriptClicks && transcriptClicks.count) || 0
      }
    })
  }

  syncRecordings = debounce(() => {
    const eplId = this.props.form.values.id

    if (!eplId) return

    eplApi
      .fetchRecordingsFromEplId(eplId)
      .then(() => {
        eplApi.getZoomRecordingCount(eplId).then(({total, video, audio, transcript}) => {
          this.recordingCounts = {total, video, audio, transcript}
          this.props.growlStore.showSuccess('Sync successful')
        })
      })
      .catch(err => {
        this.props.growlStore.showError(err)
      })
  }, SYNC_RECORDINGS_DEBOUNCE_DELAY_IN_MS)

  loadRecordings = action(eplId => {
    this.loading = true
    this.error = false

    eplApi
      .getZoomRecordingCount(eplId)
      .then(({total, video, audio, transcript}) => {
        this.recordingCounts = {total, video, audio, transcript}
        this.loading = false
      })
      .catch(e => {
        this.loading = false
        this.error = e
      })
  })

  render () {
    const { recordingCounts, loading, error } = this
    const { epl, recordingEnd } = this.props

    if (loading || error) return null

    const recordingsExist = recordingCounts.total.valueOf() !== 0

    const types = [
      {
        label: 'Audio',
        name: 'recordingAudio',
        metrics: [
          {
            value: this.recordingCounts.audio,
            label: 'existing recording'
          },
          {
            value: this.recordingClickCounts.audio,
            label: 'clicks recorded on links'
          }
        ],
        ToggleVisibility: <ToggleVisibility
          invisible={this.props.fieldHiddenToExpert.recordingAudio}
          onToggle={() => this.props.toggleFieldsHiddenToExpert('recordingAudio')}
          cyData={'toggle-hide-recording-audio'}
        />
      },
      {
        label: 'Video',
        name: 'recordingVideo',
        metrics: [
          {
            value: this.recordingCounts.video,
            label: 'existing recording'
          },
          {
            value: this.recordingClickCounts.video,
            label: 'clicks recorded on links'
          }
        ],
        ToggleVisibility: <ToggleVisibility
          invisible={this.props.fieldHiddenToExpert.recordingVideo}
          onToggle={() => this.props.toggleFieldsHiddenToExpert('recordingVideo')}
          cyData={'toggle-hide-recording-video'}
        />
      },
      {
        label: 'Transcript',
        name: 'recordingTranscript',
        metrics: [
          {
            value: this.recordingCounts.transcript,
            label: 'existing recording'
          },
          {
            value: this.recordingClickCounts.transcript,
            label: 'clicks recorded on links'
          }
        ],
        ToggleVisibility: <ToggleVisibility
          invisible={this.props.fieldHiddenToExpert.recordingTranscript}
          onToggle={() => this.props.toggleFieldsHiddenToExpert('recordingTranscript')}
          cyData={'toggle-hide-recording-transcript'}
        />
      }
    ]

    return (
      <div className='expert-form__section'>
        <h2 className='expert-form__subtitle'>Recordings</h2>
        <div className='field-toggle-group'>
          <div className="recordings">
            {recordingsExist ? (
              <div className="recordings__list">
                <div className="recordings__bulk_toggle" style={{display: 'flex', alignItems: 'baseline'}}>
                  <div class="expert-form__label">Show all interview recording links</div>
                  <ToggleVisibility
                    invisible={this.props.bulkToggleRecordingsHiddenToExpert}
                    onToggle={() => {
                      const fieldNames = types.map(type => type.name)
                      this.props.onBulkToggleRecordingsHiddenToExpert(fieldNames)
                    }}
                    cyData={'toggle-hide-recording-bulk-toggle'}
                  />
                </div>
                <RecordingTypes
                  types={types}
                  recordingCounts={recordingCounts}
                  recordingEnd={recordingEnd} />
              </div>
            ) : (
              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <RecordingsProcessing
                  eplStatus={epl.epl.eplStatus.name}
                  recordingsCount={recordingCounts.total} />
                <ToggleVisibility
                  invisible={this.props.fieldHiddenToExpert.recordings}
                  onToggle={() => this.props.toggleFieldsHiddenToExpert('recordings')}
                  cyData={'toggle-hide-recordings'}
                />
              </div>
            )}

            <Button
              style={{ marginTop: 12 }}
              onClick={this.syncRecordings}
              type='button'
            >
              <span>Sync recordings</span>
            </Button>
            <Tooltip tooltip='Recordings will be stored automatically once their video is encoded in Zoom, but this button will trigger the storage again, if the process had failed for whatever reason.' />
          </div>
        </div>
      </div>
    )
  }
}

const RecordingTypes = ({types, recordingCounts, recordingEnd}) => {
  return (
    <div className='recordings__types'>
      {types.map(type => {
        const recordingType = type.label.toLowerCase()
        const label = !recordingCounts[recordingType]
          ? setProcessingMessage(recordingEnd, recordingType) : type.label

        return (
          <div style={{display: 'flex', alignItems: 'baseline'}}>
            <div class="expert-form__label">{label}</div>
            <div class="metrics">
              {type.metrics.map(metric => <div>{metric.value} {metric.label}</div>)}
            </div>
            {type.ToggleVisibility}
          </div>
        )
      })}
    </div>
  )
}

function setProcessingMessage (recordingEnd, type) {
  const end = moment(recordingEnd)
  const today = moment()
  const difference = today.diff(end, 'days')
  const message =
    difference > 0 ? `Unable to process ${type}` : `Processing ${type}`
  return message
}

export default inject('growlStore')(observer(RecordingsSection))
